import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Search, Plus } from 'lucide-react';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'manager' | 'member';
  avatar?: string;
  joinedDate: string;
  lastActive: string;
  status: 'active' | 'inactive';
}

interface AssigneeSelectorProps {
  currentAssignees?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  }[];
  onAssigneesChange: (assignees: any[]) => void;
  teamMembers: TeamMember[];
  dropdownDirection?: 'top' | 'bottom';
  excludeUserIds?: string[];
}

export const AssigneeSelector: React.FC<AssigneeSelectorProps> = ({
  currentAssignees = [],
  onAssigneesChange,
  teamMembers,
  dropdownDirection = 'bottom',
  excludeUserIds = []
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredMembers = teamMembers.filter(member => {
    const isAlreadyAssigned = currentAssignees.some(assignee => assignee.id === member.id);
    const isExcluded = excludeUserIds.includes(member.id);
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         `@${member.name.toLowerCase().replace(/\s+/g, '')}`.includes(searchQuery.toLowerCase());

    return !isAlreadyAssigned && !isExcluded && matchesSearch;
  });

  const handleAssigneeAdd = (member: TeamMember) => {
    const newAssignee = {
      id: member.id,
      name: member.name,
      email: member.email,
      avatar: member.avatar
    };

    const updatedAssignees = [...currentAssignees, newAssignee];
    onAssigneesChange(updatedAssignees);
    setSearchQuery('');
    setIsOpen(false);
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          className="h-8 px-2 text-sm font-normal justify-start bg-workspace-accent hover:bg-workspace-accent-hover text-white"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 bg-popover border-border text-foreground"
        align="start"
        side={dropdownDirection === 'top' ? 'top' : 'bottom'}
      >
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or @username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 focus:ring-0 bg-background border-input text-foreground placeholder:text-muted-foreground"
            />
          </div>
        </div>

        <div className="max-h-60 overflow-y-auto">
          {filteredMembers.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {searchQuery ? 'No team members found' : 'All team members are already assigned'}
            </div>
          ) : (
            filteredMembers.map((member) => (
              <button
                key={member.id}
                onClick={() => handleAssigneeAdd(member)}
                className="w-full p-3 text-left transition-colors hover:bg-muted"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={member.avatar} alt={member.name} />
                    <AvatarFallback className="text-xs">
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-foreground">
                      {member.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {member.email}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="text-xs capitalize border-border text-muted-foreground bg-muted/50"
                    >
                      {member.role}
                    </Badge>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
